import React from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useAuthentication } from "@/contexts"
import { FormAutocomplete } from "@/nzds/forms/form-autocomplete"
import { useAuthorizedOrganizations } from "@/services/organization/useAuthorizedOrganizations"

import Chip from "@mui/material/Chip"

import {
  normalizeOrganizationIds,
  resolveOrganizationIds,
} from "./organization-options"

interface OrganizationOption {
  id: string
  label: string
}

interface UserOrganizationFormValues {
  organizationIds: string[]
}

interface UserOrganizationFieldsProps {
  disabled?: boolean
}

export const UserOrganizationFields: React.FC<UserOrganizationFieldsProps> = ({
  disabled = false,
}) => {
  const { t } = useTranslation("common")
  const { activeUser, isAdminWriterUser } = useAuthentication()
  const { setValue, watch } = useFormContext<UserOrganizationFormValues>()
  const { authorizedOrganizationsData: authorizedOrganizations = [] } =
    useAuthorizedOrganizations()

  const actorOrganizationIds = new Set(
    (activeUser?.organizationIds ?? []).map(String)
  )
  const selectedOrganizationIds = watch("organizationIds")

  const organizationsById = new Map(
    authorizedOrganizations
      .filter((organization) => organization.id)
      .map((organization) => [String(organization.id), organization] as const)
  )

  const selectedOrganizationOptions: OrganizationOption[] =
    selectedOrganizationIds.map((organizationId) => ({
      id: organizationId,
      label:
        organizationsById.get(organizationId)?.name ??
        t("editUser.organizationUnknownLabel"),
    }))

  const organizationAutocompleteOptions: OrganizationOption[] =
    authorizedOrganizations
      .filter((organization) => organization.id)
      .map((organization) => ({
        id: String(organization.id),
        label: organization.name,
      }))

  const isSelectionLocked = (organizationId: string): boolean =>
    !isAdminWriterUser && !actorOrganizationIds.has(organizationId)

  return (
    <FormAutocomplete<
      UserOrganizationFormValues,
      "organizationIds",
      OrganizationOption,
      true
    >
      disableCloseOnSelect
      disabled={disabled}
      fullWidth
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      label={t("editUser.organizationPickerLabel")}
      multiple
      name="organizationIds"
      noOptionsText={t("editUser.organizationPickerNoOptions")}
      onChange={(_event, selectedOrganizations) => {
        setValue(
          "organizationIds",
          resolveOrganizationIds({
            actorOrganizationIds,
            isAdminWriterUser,
            nextIds: normalizeOrganizationIds(
              selectedOrganizations
                .map((organization) => organization.id)
                .filter(Boolean)
            ),
            previouslySelectedIds: selectedOrganizationIds,
          }),
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        )
      }}
      options={organizationAutocompleteOptions}
      placeholder={t("editUser.organizationPickerPlaceholder")}
      renderValue={(organizations, getItemProps) =>
        organizations.map((organization, index) => {
          const { key, ...itemProps } = getItemProps({ index })
          const chipDisabled = isSelectionLocked(organization.id)

          return (
            <li key={organization.id} style={{ listStyle: "none" }}>
              <Chip
                key={key}
                {...itemProps}
                disabled={chipDisabled}
                label={organization.label}
                onDelete={chipDisabled ? undefined : itemProps.onDelete}
                variant="outlined"
              />
            </li>
          )
        })
      }
      value={selectedOrganizationOptions}
      variant="stacked"
    />
  )
}
